import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, RotateCcw, X } from "lucide-react";

// Configuration modal. Opened from the bottom nav "Settings" tab (or
// programmatically via the `open` prop).
export const ConfigMenu = ({ open, onClose, soundEnabled, onToggleSound, onReset }) => {
  const [confirmReset, setConfirmReset] = useState(false);

  const doReset = () => {
    onReset();
    setConfirmReset(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={onClose}
          data-testid="config-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-md p-6 flex flex-col gap-4"
            style={{
              backgroundColor: "#0d0d0d",
              border: "1px solid #FFD400",
              borderRadius: 4,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="config-panel"
          >
            <div className="flex items-center justify-between">
              <span
                className="text-sm font-black uppercase tracking-[0.3em]"
                style={{ color: "#FFD400", fontFamily: "Orbitron, sans-serif" }}
              >
                Settings
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="config-close"
                className="text-white/60 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <button
              type="button"
              onClick={onToggleSound}
              data-testid="sound-toggle"
              className="flex items-center justify-between px-4 py-3 border border-[#2a2a2a] hover:border-[#FFD400] transition-colors text-left"
              style={{ borderRadius: 2 }}
            >
              <div className="flex flex-col">
                <span
                  className="text-xs uppercase tracking-[0.25em] text-white"
                  style={{ fontFamily: "Orbitron, sans-serif" }}
                >
                  Sound
                </span>
                <span className="text-[10px] text-white/50 mt-0.5">
                  Synthetic per-era effects
                </span>
              </div>
              {soundEnabled ? (
                <Volume2 size={18} color="#FFD400" />
              ) : (
                <VolumeX size={18} color="#666" />
              )}
            </button>

            {!confirmReset ? (
              <button
                type="button"
                onClick={() => setConfirmReset(true)}
                data-testid="reset-open"
                className="flex items-center justify-between px-4 py-3 border border-[#2a2a2a] hover:border-[#FFD400] transition-colors text-left"
                style={{ borderRadius: 2 }}
              >
                <div className="flex flex-col">
                  <span
                    className="text-xs uppercase tracking-[0.25em] text-white"
                    style={{ fontFamily: "Orbitron, sans-serif" }}
                  >
                    Reset Game
                  </span>
                  <span className="text-[10px] text-white/50 mt-0.5">
                    Wipe the save completely
                  </span>
                </div>
                <RotateCcw size={18} color="#FFD400" />
              </button>
            ) : (
              <div
                className="flex flex-col gap-3 p-4 border border-[#FFD400]"
                style={{ borderRadius: 2 }}
              >
                <span
                  className="text-xs text-white leading-snug"
                  style={{ fontFamily: "Orbitron, sans-serif" }}
                >
                  Reset the entire save? This cannot be undone.
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={doReset}
                    data-testid="reset-confirm"
                    className="flex-1 px-3 py-2.5 bg-[#FFD400] text-[#111] font-black uppercase text-xs tracking-widest hover:bg-white transition-colors"
                    style={{ fontFamily: "Orbitron, sans-serif", borderRadius: 2 }}
                  >
                    Yes, reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmReset(false)}
                    data-testid="reset-cancel"
                    className="flex-1 px-3 py-2.5 border border-[#333] text-white uppercase text-xs tracking-widest hover:border-[#FFD400] transition-colors"
                    style={{ fontFamily: "Orbitron, sans-serif", borderRadius: 2 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfigMenu;
